import { useState, useEffect } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format, getDaysInMonth, startOfMonth, getDay, addDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  Plus,
  CalendarIcon,
  Clock,
  Check,
  X,
  Send,
  FileText,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

const STATUS_CONFIG = {
  draft: { label: 'Brouillon', color: 'bg-slate-100 text-slate-700' },
  submitted: { label: 'Soumis', color: 'bg-amber-100 text-amber-700' },
  approved: { label: 'Approuvé', color: 'bg-emerald-100 text-emerald-700' },
  rejected: { label: 'Refusé', color: 'bg-red-100 text-red-700' },
};

const WEEKDAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

export default function CRAPage() {
  const { user, isManager } = useAuth();
  const [cras, setCras] = useState([]);
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedCra, setSelectedCra] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'calendar'
  
  // Form state
  const [selectedMission, setSelectedMission] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [entries, setEntries] = useState([]);
  const [comments, setComments] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [crasRes, missionsRes] = await Promise.all([
        api.get('/cra'),
        api.get('/missions'),
      ]);
      setCras(crasRes.data);
      setMissions(missionsRes.data.filter(m => m.status === 'active'));
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const initializeEntries = (month, year) => {
    const daysInMonth = getDaysInMonth(new Date(year, month - 1));
    const newEntries = [];
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month - 1, day);
      const dayOfWeek = getDay(date);
      // Skip weekends (0 = Sunday, 6 = Saturday)
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        newEntries.push({
          date: format(date, 'yyyy-MM-dd'),
          hours: 8, // Default to full day
          description: '',
        });
      }
    }
    setEntries(newEntries);
  };

  const handleCreateCRA = async () => {
    if (!selectedMission) {
      toast.error('Veuillez sélectionner une mission');
      return;
    }

    try {
      await api.post('/cra', {
        mission_id: selectedMission,
        month: selectedMonth,
        year: selectedYear,
        entries: entries,
        comments: comments,
      });
      toast.success('CRA créé avec succès');
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleUpdateCRA = async (craId, status, rejectionReason = '') => {
    try {
      await api.put(`/cra/${craId}`, { status, rejection_reason: rejectionReason });
      toast.success(`CRA ${status === 'approved' ? 'approuvé' : status === 'rejected' ? 'refusé' : 'mis à jour'}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const handleSubmitCRA = async (craId) => {
    await handleUpdateCRA(craId, 'submitted');
  };

  const resetForm = () => {
    setSelectedMission('');
    setSelectedMonth(new Date().getMonth() + 1);
    setSelectedYear(new Date().getFullYear());
    setEntries([]);
    setComments('');
  };

  const updateEntry = (index, field, value) => {
    const newEntries = [...entries];
    newEntries[index] = { ...newEntries[index], [field]: value };
    setEntries(newEntries);
  };

  const totalDays = entries.reduce((sum, e) => sum + (e.hours / 8), 0);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              Comptes Rendus d'Activité
            </h1>
            <p className="text-slate-500 mt-1">
              Gérez vos CRA et suivez le temps passé sur vos missions
            </p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-slate-900 hover:bg-slate-800" data-testid="create-cra-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nouveau CRA
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Créer un CRA</DialogTitle>
                <DialogDescription>
                  Saisissez vos heures travaillées pour le mois sélectionné
                </DialogDescription>
              </DialogHeader>
              
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Mission</Label>
                    <Select value={selectedMission} onValueChange={setSelectedMission}>
                      <SelectTrigger data-testid="cra-mission-select">
                        <SelectValue placeholder="Sélectionner une mission" />
                      </SelectTrigger>
                      <SelectContent>
                        {missions.map((mission) => (
                          <SelectItem key={mission.mission_id} value={mission.mission_id}>
                            {mission.title} - {mission.client_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Mois</Label>
                    <Select 
                      value={selectedMonth.toString()} 
                      onValueChange={(v) => {
                        setSelectedMonth(parseInt(v));
                        initializeEntries(parseInt(v), selectedYear);
                      }}
                    >
                      <SelectTrigger data-testid="cra-month-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => (
                          <SelectItem key={m} value={m.toString()}>
                            {format(new Date(2024, m - 1), 'MMMM', { locale: fr })}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Année</Label>
                    <Select 
                      value={selectedYear.toString()} 
                      onValueChange={(v) => {
                        setSelectedYear(parseInt(v));
                        initializeEntries(selectedMonth, parseInt(v));
                      }}
                    >
                      <SelectTrigger data-testid="cra-year-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[2024, 2025, 2026].map((y) => (
                          <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Button
                  type="button"
                  variant="outline"
                  onClick={() => initializeEntries(selectedMonth, selectedYear)}
                  data-testid="init-entries-btn"
                >
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  Initialiser les jours ouvrés
                </Button>

                {entries.length > 0 && (
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="w-32">Date</TableHead>
                          <TableHead className="w-24">Heures</TableHead>
                          <TableHead>Description</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {entries.map((entry, index) => (
                          <TableRow key={entry.date}>
                            <TableCell className="font-medium">
                              {format(new Date(entry.date), 'EEE dd/MM', { locale: fr })}
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                min="0"
                                max="24"
                                step="0.5"
                                value={entry.hours}
                                onChange={(e) => updateEntry(index, 'hours', parseFloat(e.target.value) || 0)}
                                className="w-20"
                                data-testid={`entry-hours-${index}`}
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                placeholder="Description (optionnel)"
                                value={entry.description}
                                onChange={(e) => updateEntry(index, 'description', e.target.value)}
                                data-testid={`entry-desc-${index}`}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <span className="font-medium">Total:</span>
                  <span className="text-xl font-bold text-slate-900">{totalDays.toFixed(1)} jours</span>
                </div>

                <div className="space-y-2">
                  <Label>Commentaires</Label>
                  <Textarea
                    placeholder="Remarques ou informations complémentaires..."
                    value={comments}
                    onChange={(e) => setComments(e.target.value)}
                    data-testid="cra-comments"
                  />
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Annuler
                </Button>
                <Button onClick={handleCreateCRA} className="bg-slate-900 hover:bg-slate-800" data-testid="save-cra-btn">
                  Créer le CRA
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* CRA List */}
        <Card>
          <CardHeader>
            <CardTitle>Mes CRA</CardTitle>
          </CardHeader>
          <CardContent>
            {cras.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Aucun CRA pour le moment</p>
                <p className="text-sm text-slate-400 mt-1">
                  Créez votre premier CRA pour commencer
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Période</TableHead>
                      <TableHead>Mission</TableHead>
                      <TableHead>Client</TableHead>
                      <TableHead className="text-center">Jours</TableHead>
                      <TableHead className="text-center">Montant</TableHead>
                      <TableHead className="text-center">Statut</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {cras.map((cra) => (
                      <TableRow key={cra.cra_id} data-testid={`cra-row-${cra.cra_id}`}>
                        <TableCell className="font-medium">
                          {format(new Date(cra.year, cra.month - 1), 'MMMM yyyy', { locale: fr })}
                        </TableCell>
                        <TableCell>{cra.mission_title}</TableCell>
                        <TableCell>{cra.client_name}</TableCell>
                        <TableCell className="text-center font-mono">
                          {cra.total_days?.toFixed(1)}
                        </TableCell>
                        <TableCell className="text-center font-mono">
                          {new Intl.NumberFormat('fr-FR', {
                            style: 'currency',
                            currency: 'EUR',
                          }).format((cra.total_days || 0) * (cra.tjm || 0))}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge className={STATUS_CONFIG[cra.status]?.color}>
                            {STATUS_CONFIG[cra.status]?.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            {cra.status === 'draft' && (
                              <Button
                                size="sm"
                                onClick={() => handleSubmitCRA(cra.cra_id)}
                                data-testid={`submit-cra-${cra.cra_id}`}
                              >
                                <Send className="w-4 h-4 mr-1" />
                                Soumettre
                              </Button>
                            )}
                            {isManager && cra.status === 'submitted' && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-emerald-600"
                                  onClick={() => handleUpdateCRA(cra.cra_id, 'approved')}
                                  data-testid={`approve-cra-${cra.cra_id}`}
                                >
                                  <Check className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-red-600"
                                  onClick={() => handleUpdateCRA(cra.cra_id, 'rejected', 'Veuillez corriger les heures')}
                                  data-testid={`reject-cra-${cra.cra_id}`}
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
